import { Link, useParams } from 'react-router';

import { ApiError, type CoveragePeriodResponse, type SalaryVersionResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  EmptyState,
  PageHeader,
  Panel,
  type Column,
} from '../../components';
import {
  EMPTY_VALUE,
  formatCpf,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDecimal,
} from '../../format';
import { PATHS } from '../../routes';
import { usePersonHistory, usePortalContracts } from '../queries';
import { useCompanyScope } from './companyScope';
import { PORTAL_COPY } from './portal';

/**
 * One person, and everything the contract has ever recorded about them.
 *
 * This is the portal's only screen addressed by an identifier, and it is where
 * "a user cannot reach anybody belonging to a company they are not entitled to,
 * including by direct URL" is actually tested. Three things hold it up:
 *
 *  - The identifier in the path is the opaque `insured_person_id` and nothing
 *    else. No CPF, no name -- a URL survives in history, in a bookmark, in a
 *    screen share and in the referrer of anything the page loads.
 *  - The contract the lookup is made against comes from the *selected company*,
 *    never from the URL, so there is no address that names a tenant.
 *  - The backend scopes the query before the handler runs. A person of another
 *    company is a `404` -- the same answer an invented identifier gets, which
 *    is what stops the refusal from confirming that the person exists.
 *
 * So a pasted link to somebody else's employee renders the refusal below, with
 * no name, no CPF and no date of birth having been anywhere near the browser.
 */

const COVERAGE_COLUMNS: readonly Column<CoveragePeriodResponse>[] = [
  {
    key: 'effective_from',
    header: 'Início',
    align: 'end',
    render: (period) => formatDate(period.effective_from),
  },
  {
    key: 'effective_to',
    header: 'Fim',
    align: 'end',
    render: (period) => formatDate(period.effective_to),
  },
  {
    key: 'created_at',
    header: 'Registrado em',
    align: 'end',
    render: (period) => formatDateTime(period.created_at),
  },
];

const SALARY_COLUMNS: readonly Column<SalaryVersionResponse>[] = [
  {
    key: 'effective_from',
    header: 'A partir de',
    align: 'end',
    render: (version) => formatDate(version.effective_from),
  },
  {
    key: 'salary',
    header: 'Salário',
    align: 'end',
    render: (version) => formatCurrency(version.salary),
  },
  {
    key: 'created_at',
    header: 'Registrado em',
    align: 'end',
    render: (version) => formatDateTime(version.created_at),
  },
];

function BackToRoster() {
  return <Link to={PATHS.customerBeneficiaries}>{PORTAL_COPY.backToRoster}</Link>;
}

export function PersonHistoryPage() {
  const { pessoaId = '' } = useParams();
  const { company } = useCompanyScope();
  const contracts = usePortalContracts(company.id);
  // A customer has one contract today. `noUncheckedIndexedAccess` makes the
  // "or none yet" case something every read below has to handle, which is
  // what keeps a company still being onboarded off a broken screen.
  const contract = contracts.data?.items[0];
  const history = usePersonHistory(company.id, contract?.id, pessoaId);

  // A refusal is not a failure to load, and saying "não foi possível carregar"
  // to somebody who followed a stale or foreign link would send them to press
  // retry forever.
  if (
    history.error instanceof ApiError &&
    (history.error.isNotFound || history.error.isForbidden)
  ) {
    return (
      <div className="page">
        <PageHeader title={PORTAL_COPY.personHistoryTitle} />
        <EmptyState
          title={PORTAL_COPY.personNotFound}
          description={PORTAL_COPY.personNotFoundHint}
          action={<BackToRoster />}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <AsyncSection
        state={history}
        loadingLabel="Carregando histórico…"
        errorTitle="Não foi possível carregar o histórico"
      >
        {(data) => (
          <>
            <PageHeader
              title={data.person.full_name}
              description={`CPF ${formatCpf(data.person.cpf)} · nascimento em ${formatDate(
                data.person.birth_date,
              )}`}
              actions={<BackToRoster />}
            />

            {/* Read-only on purpose: these are the insurer's figures, recorded by
                vyttech, and nothing on a customer screen changes them. */}
            <Panel
              title="Dados da seguradora"
              description="Como a seguradora registra esta pessoa. Informados pela vyttech."
            >
              <dl className="definitions">
                <div>
                  <dt>Certificado</dt>
                  <dd>{data.person.certificate_number ?? EMPTY_VALUE}</dd>
                </div>
                <div>
                  <dt>IS Segurado</dt>
                  <dd>{formatDecimal(data.person.insured_capital)}</dd>
                </div>
                <div>
                  <dt>Prêmio</dt>
                  <dd>{formatDecimal(data.person.premium)}</dd>
                </div>
              </dl>
            </Panel>

            <Panel
              title="Períodos de cobertura"
              description="Todos os períodos já registrados, inclusive os encerrados. As datas são inclusivas nas duas pontas."
            >
              <DataTable
                caption="Períodos de cobertura desta pessoa"
                columnVisibility={false}
                columns={COVERAGE_COLUMNS}
                rows={data.coverage_periods}
                rowKey={(period) => period.id}
                emptyTitle="Nenhum período de cobertura registrado"
              />
            </Panel>

            <Panel
              title="Salários"
              description="Todas as versões já registradas, da mais antiga à mais recente."
            >
              <DataTable
                caption="Versões de salário desta pessoa"
                columnVisibility={false}
                columns={SALARY_COLUMNS}
                rows={data.salary_versions}
                rowKey={(version) => version.id}
                emptyTitle="Nenhum salário registrado"
              />
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}
